import {
  Alert,
  Badge,
  buttonClass,
  Card,
  Cell,
  formatDate,
  Loading,
  PageHeader,
  Table,
} from '@avrahami/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useCan } from '../api/session';
import { contentPagesQuery, useStoreTimeZone } from '../api/tenant';
import { fill, he } from '../i18n/he';
import { missingStandardPages } from '../lib/access';
import { errorMessage } from '../lib/forms';

/** The store's content pages, with one-click "create" for standard pages it does not have. */
export function ContentPagesPage() {
  const canWrite = useCan('store.settings.write');
  const pages = useQuery(contentPagesQuery());
  const timeZone = useStoreTimeZone();
  const items = pages.data?.items ?? [];
  const missing = pages.data === undefined ? [] : missingStandardPages(items);

  return (
    <>
      <PageHeader
        title={he.pages.title}
        actions={
          canWrite ? (
            <Link
              to="/pages/new"
              search={{ kind: 'CUSTOM' }}
              className={buttonClass()}
              data-testid="page-create"
            >
              {he.pages.create}
            </Link>
          ) : null
        }
      />
      <div className="flex flex-col gap-6">
        {canWrite && missing.length > 0 ? (
          <Card>
            <p className="mb-3 text-sm text-slate-600">{he.pages.missing}</p>
            <div className="flex flex-wrap gap-2">
              {missing.map((kind) => (
                <Link
                  key={kind}
                  to="/pages/new"
                  search={{ kind }}
                  data-testid="page-create-kind"
                  data-kind={kind}
                  className={buttonClass('secondary')}
                >
                  {fill(he.pages.createKind, { kind: he.pages.kinds[kind] })}
                </Link>
              ))}
            </div>
          </Card>
        ) : null}
        <Card>
          {pages.isPending ? <Loading /> : null}
          {pages.isError ? <Alert>{errorMessage(pages.error)}</Alert> : null}
          {pages.isSuccess && items.length === 0 ? (
            <p className="text-sm text-slate-500">{he.pages.empty}</p>
          ) : null}
          {items.length > 0 ? (
            <Table
              head={[
                he.pages.titleField,
                he.pages.kind,
                he.pages.slug,
                he.common.status,
                he.common.position,
                he.pages.updated,
              ]}
            >
              {items.map((page) => (
                <tr key={page.id} data-testid="content-page-row" data-slug={page.slug}>
                  <Cell>
                    <Link
                      to="/pages/$pageId"
                      params={{ pageId: page.id }}
                      className="font-medium text-brand-700 hover:underline"
                    >
                      {page.title}
                    </Link>
                  </Cell>
                  <Cell>{he.pages.kinds[page.kind]}</Cell>
                  <Cell className="ltr">/{page.slug}</Cell>
                  <Cell>
                    <Badge tone={page.isPublished ? 'green' : 'slate'}>
                      {page.isPublished ? he.pages.published : he.pages.draft}
                    </Badge>
                  </Cell>
                  <Cell>{page.position}</Cell>
                  <Cell>{formatDate(page.updatedAt, timeZone)}</Cell>
                </tr>
              ))}
            </Table>
          ) : null}
        </Card>
      </div>
    </>
  );
}
